import { useCallback, useMemo, useRef, useState } from "react";
import {
  getDocs,
  onSnapshot,
  limit,
  orderBy,
  query,
  startAfter,
  where,
} from "../firebase";
import { filterUndefinedValues } from "@markit/common.utils";
import {
  AccountData,
  DMAutomationSubmission,
  Follower,
} from "@markit/common.types";
import { getUserDmAutomationSubmissionsRef } from "../utils/FirebaseUtils";
import { fetchSingleFollower, fetchSingleUser } from "../utils/FetchSingleData";
import { loadAlgoliaUsersSearchResults } from "../utils/algoliaUtils";
import { useSelector } from "react-redux";
import { getAccountState } from "../redux/slices/accountSlice";
import useOnUnmount from "./useOnUnmount";

type useLoadDMAutomationSubmissionsProps = {
  creatorUid: string;
  dmAutomationId: string;
  windowSize: number;
};

export const useLoadDMAutomationSubmissions = (
  props: useLoadDMAutomationSubmissionsProps
) => {
  const { creatorUid, dmAutomationId, windowSize } = props;
  const { followingAccountData } = useSelector(getAccountState).account;

  const [fetchedSubmissions, setFetchedSubmissions] = useState<
    DMAutomationSubmission[]
  >([]);
  const [fetchedUserData, setFetchedUserData] = useState<AccountData[]>([]);
  const [fetchedFollowerData, setFetchedFollowerData] = useState<Follower[]>(
    []
  );
  const [isLoading, setIsLoading] = useState(false);
  const [loadedAll, setLoadedAll] = useState(false);
  const [lastVisible, setLastVisible] = useState("");
  const unsubscribeArrayRef = useRef<(() => void)[]>([]);
  const searchTimeoutRef = useRef<NodeJS.Timeout | undefined>(undefined);

  useOnUnmount(() => {
    unsubscribeArrayRef.current.forEach(
      (unsubscribe) => unsubscribe && unsubscribe()
    );
    unsubscribeArrayRef.current = [];
  }, []);

  const isFinished = useMemo(() => loadedAll, [loadedAll]);

  const addUserData = useCallback((newUserData: AccountData[]) => {
    setFetchedUserData((prev) => {
      const userMap = new Map(prev.map((user) => [user.uid, user]));
      newUserData.forEach((user) => userMap.set(user.uid, user));
      return Array.from(userMap.values());
    });
  }, []);

  const addFollowerData = useCallback((newFollowerData: Follower[]) => {
    setFetchedFollowerData((prev) => {
      const followerMap = new Map(
        prev.map((follower) => [follower.uid, follower])
      );
      newFollowerData.forEach((follower) =>
        followerMap.set(follower.uid, follower)
      );
      return Array.from(followerMap.values());
    });
  }, []);

  const addSubmissionData = useCallback((newData: DMAutomationSubmission[]) => {
    setFetchedSubmissions((prev) => {
      const submissionMap = new Map(prev.map((s) => [s.id, s]));
      newData.forEach((s) => submissionMap.set(s.id, s));
      return Array.from(submissionMap.values()).sort(
        (a, b) =>
          new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
      );
    });
  }, []);

  const removeSubmissionData = useCallback((data: DMAutomationSubmission) => {
    setFetchedSubmissions((prev) => prev.filter((s) => s.id !== data.id));
  }, []);

  const updateData = useCallback(
    async (found: DMAutomationSubmission[]) => {
      addSubmissionData(found);
      const users = await Promise.all(
        found.map((submission) =>
          submission.uid
            ? fetchSingleUser(submission.uid, followingAccountData)
            : Promise.resolve(undefined)
        )
      );
      addUserData(filterUndefinedValues(users));
      const followers = await Promise.all(
        found.map((submission) =>
          submission.uid
            ? fetchSingleFollower(creatorUid, submission.uid, [])
            : Promise.resolve(undefined)
        )
      );
      addFollowerData(filterUndefinedValues(followers));
    },
    [
      addFollowerData,
      addSubmissionData,
      addUserData,
      creatorUid,
      followingAccountData,
    ]
  );

  const initialUpdateData = useCallback(
    async (found: DMAutomationSubmission[]) => {
      if (found.length) {
        setLastVisible(found[found.length - 1].createdAt);
      }
      if (found.length < windowSize) {
        setLoadedAll(true);
      }
      if (found.length > 0) {
        await updateData(found);
      }
    },
    [updateData, windowSize]
  );

  const liveUpdateData = useCallback(
    (
      type: "added" | "modified" | "removed",
      submission: DMAutomationSubmission,
      found: DMAutomationSubmission[]
    ) => {
      if (
        (type === "added" && !found.some((f) => f.id === submission.id)) ||
        type === "modified"
      ) {
        updateData([submission]);
      } else if (type === "removed") {
        removeSubmissionData(submission);
      }
    },
    [removeSubmissionData, updateData]
  );

  const loadSubmissions = useCallback(async () => {
    if (isLoading) {
      return;
    }
    setIsLoading(true);
    const submissionsRef = getUserDmAutomationSubmissionsRef(
      creatorUid,
      dmAutomationId
    );
    const submissionsQuery = query(
      submissionsRef,
      orderBy("createdAt", "desc"),
      limit(windowSize)
    );
    const found = (await getDocs(submissionsQuery)).docs.map((doc) =>
      doc.data()
    );
    await initialUpdateData(found);

    const unsubscribe = onSnapshot(submissionsQuery, (snapshot) => {
      snapshot.docChanges().forEach((message) => {
        liveUpdateData(message.type, message.doc.data(), found);
      });
    });
    unsubscribeArrayRef.current.push(unsubscribe);
    setIsLoading(false);
  }, [
    creatorUid,
    dmAutomationId,
    initialUpdateData,
    isLoading,
    liveUpdateData,
    windowSize,
  ]);

  const loadMoreSubmissions = useCallback(async () => {
    if (isFinished || isLoading || !lastVisible) {
      return;
    }
    setIsLoading(true);
    const submissionsRef = getUserDmAutomationSubmissionsRef(
      creatorUid,
      dmAutomationId
    );
    const submissionsQuery = query(
      submissionsRef,
      orderBy("createdAt", "desc"),
      startAfter(lastVisible),
      limit(windowSize)
    );
    const found = (await getDocs(submissionsQuery)).docs.map((doc) =>
      doc.data()
    );
    await initialUpdateData(found);

    const unsubscribe = onSnapshot(submissionsQuery, (snapshot) => {
      snapshot.docChanges().forEach((message) => {
        liveUpdateData(message.type, message.doc.data(), found);
      });
    });
    unsubscribeArrayRef.current.push(unsubscribe);
    setIsLoading(false);
  }, [
    creatorUid,
    dmAutomationId,
    initialUpdateData,
    isFinished,
    isLoading,
    lastVisible,
    liveUpdateData,
    windowSize,
  ]);

  const checkIfAddUserData = useCallback(
    async (uid: string) => {
      const userData = await fetchSingleUser(uid, followingAccountData);
      if (userData) {
        addUserData([userData]);
      }
      const followerData = await fetchSingleFollower(creatorUid, uid, []);
      if (followerData) {
        addFollowerData([followerData]);
      }
    },
    [addFollowerData, addUserData, creatorUid, followingAccountData]
  );

  // Subscribes to this uid's submissions directly (targeted query), so a
  // specific person's submission is fetched even if it hasn't been paginated
  // into fetchedSubmissions yet. Used by name search (fanned out over the
  // Algolia-resolved uid list) and by phone search (a single resolved uid).
  const loadSubmissionsForUid = useCallback(
    (uid: string) => {
      const submissionsRef = getUserDmAutomationSubmissionsRef(
        creatorUid,
        dmAutomationId
      );
      const searchQuery = query(submissionsRef, where("uid", "==", uid));
      const unsubscribe = onSnapshot(searchQuery, (snapshot) => {
        snapshot.docChanges().forEach((message) => {
          if (message.type === "added" || message.type === "modified") {
            addSubmissionData([message.doc.data()]);
            checkIfAddUserData(uid);
          } else if (message.type === "removed") {
            removeSubmissionData(message.doc.data());
          }
        });
      });
      unsubscribeArrayRef.current.push(unsubscribe);
      return unsubscribe;
    },
    [addSubmissionData, checkIfAddUserData, creatorUid, dmAutomationId, removeSubmissionData]
  );

  const loadSearchResults = useCallback(
    async (searchTerm: string) => {
      const fetchSubmissionUsers = (userIds: string[]) => {
        userIds.forEach((uid) => loadSubmissionsForUid(uid));
      };
      await loadAlgoliaUsersSearchResults(
        searchTerm,
        `following:${creatorUid}`,
        fetchSubmissionUsers
      );
    },
    [creatorUid, loadSubmissionsForUid]
  );

  const loadSearchResultsThrottled = useCallback(
    (searchTerm: string) => {
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current);
      }
      searchTimeoutRef.current = setTimeout(() => {
        if (searchTerm.length > 1) {
          loadSearchResults(searchTerm);
        }
      }, 500);
    },
    [loadSearchResults]
  );

  return {
    fetchedSubmissions,
    fetchedUserData,
    fetchedFollowerData,
    isLoading,
    isFinished,
    loadSubmissions,
    loadMoreSubmissions,
    loadSearchResultsThrottled,
    loadSubmissionsForUid,
  };
};
